import React, { useState } from "react";
import { Divider, Stack } from "@mui/material";
import i18n from 'translation';
import { QueryInfo } from "system/query/QueryInfo";
import { AutoWidgetQueryBehavior, WidgetRequest } from "system/widget/Widget";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import RadialSentimentChart from "./RadialSentimentChart";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";


const defaultWidgetRequest = {
  name: 'radial-sentiment',
  endpoint: 'query/agg',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultRadialSentimentRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundSentimentField = queryInfo.fields?.find((field) => field.id === "sentiment") || false;
  if (!foundSentimentField) {
    return undefined;
  }

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

  const requestInitBody = { aggs: {} };
  requestInitBody.aggs = {
      "sentiment": {
        "op": "keyword",
        "field": "sentiment",
          "aggs": {
            "_percent": {
              "op": "percent",
              "of_total": true
            }
          }
      }
    };

  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export type RadialSentimentSettingsStateType = {
  customWidgetTitle: string;
  showLegend: boolean;
  widgetTitle: string;
  widgetTitleAlignment: string;
};

export const defaultSettings: RadialSentimentSettingsStateType = {
  customWidgetTitle: '',
  showLegend: true,
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
};

export default function RadialSentimentSettings(props: WidgetSettingsProps): JSX.Element {
  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

  const [settingsState, setSettingsState] = useState<RadialSentimentSettingsStateType>({...defaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: RadialSentimentSettingsStateType) => setSettingsState(updatedState);

  const updateWidgetData = () => {
    props.saveWidgetData({ settings: settingsState }).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  };

  return (
    <TabbedDialog
      title={i18n.t("widget-types.radial-sentiment.settings.title")}
      tabs={[`${i18n.t("widgets.settings.display-tab")}`]}
      open={true}
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidgetData();
        props.setSettingsDialogIsOpen(false);
      }}>
      <>
      {/* Tab #1 */}
      {tabValue === 0 &&
        <SplitSettingsWrapper>
          {/* Widget Graph Preview */}
          <RadialSentimentChart settingsState={settingsState} isPreview={true} />
          {/* Settings Options */}
          <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
            <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
              {/* Widget Title */}
              <SettingsWidgetTitleSelect
                keyid="widgetTitle"
                label={i18n.t("title")}
                showQueryOption={true}
                widgetTitleValue={settingsState.widgetTitle} />
              {settingsState.widgetTitle === 'custom' &&
                <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                  <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                  <SettingsTextField
                    keyid="customWidgetTitle"
                    label={i18n.t("widget-settings.custom-text")}
                    value={settingsState.customWidgetTitle} />
                </Stack>}
              <SettingsToggleButton
                keyid="widgetTitleAlignment"
                label={i18n.t("widget-settings.alignment")}
                optionsList={horizontalAlignmentOptions}
                value={settingsState.widgetTitleAlignment} />
            </SettingsAccordion>
            <SettingsCheckbox
              checked={settingsState.showLegend}
              keyid="showLegend"
              label={i18n.t("widget-settings.show-legend")} />
          </SettingsOptions>
        </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  );
}
